/**
 * Tool Contracts
 *
 * oRPC contract definitions for all AI tools.
 * These contracts serve as the single source of truth for:
 * - Input validation schemas
 * - Output type schemas
 * - Tool descriptions (for AI consumption)
 * - Tool metadata (strict mode, etc.)
 *
 * Usage:
 * 1. Import contracts in shared-types for frontend type inference
 * 2. Use implementTool() on server to create AI SDK tools from contracts
 *
 * @example
 * ```typescript
 * // On server
 * import { implementTool, TOOL_CONTRACTS } from '../index'
 *
 * const scheduleTool = implementTool(TOOL_CONTRACTS.scheduleEvent.contract, {
 *   execute: async (input) => await scheduleService.schedule(ctx.phoneE164, input)
 * })
 * ```
 */

import {
	type AnySchema,
	type ContractProcedure,
	type ErrorMap,
	oc,
} from "@orpc/contract";
import { AI_SDK_TOOL_META_SYMBOL, type AiSdkToolMeta } from "./implement-tool";
import type { AllowableCategories, ToolCategory } from "./tool-contract";
// Input schemas
import {
	cancelEventInputSchema,
	cancelEventRangeInputSchema,
	cancelEventsInputSchema,
	collectLeadInputSchema,
	generateInsightsReportInputSchema,
	getAgentSettingsInputSchema,
	getAnalyticsInputSchema,
	getAvailableSlotsBatchInputSchema,
	getAvailableTimeSlotsInputSchema,
	getChannelStatusInputSchema,
	getContactSnapshotInputSchema,
	getConversationMetricsInputSchema,
	getCurrentDatetimeInputSchema,
	getDocumentSchemasInputSchema,
	getEventSnapshotsInputSchema,
	getKnowledgeGapsInputSchema,
	getUsageBreakdownInputSchema,
	getUserDataInputSchema,
	handoffToHumanInputSchema,
	ingestUploadedFileInputSchema,
	joinWaitlistInputSchema,
	manageAutomationInputSchema,
	manageBeneficiaryInputSchema,
	manageContactInputSchema,
	manageKnowledgeSourceInputSchema,
	modifyEventInputSchema,
	modifyEventRangeInputSchema,
	modifyEventsInputSchema,
	queryConversationsInputSchema,
	queryStructuredDataInputSchema,
	renderChartInputSchema,
	requestPhoneNumberInputSchema,
	saveFeedbackInputSchema,
	scheduleEventInputSchema,
	scheduleEventsInputSchema,
	searchAvailableAppointmentsInputSchema,
	searchConversationsInputSchema,
	searchDocsInputSchema,
	sendBusinessLocationInputSchema,
	sendInteractiveButtonsInputSchema,
	sendInteractiveListInputSchema,
	startIntegrationConnectionInputSchema,
	summarizeConversationInputSchema,
	triggerRetrainingInputSchema,
} from "./tool-input-schemas";
// Output schemas
import {
	cancelEventRangeOutputSchema,
	cancelEventsOutputSchema,
	generateInsightsReportOutputSchema,
	getAgentSettingsOutputSchema,
	getAnalyticsOutputSchema,
	getAvailableSlotsBatchOutputSchema,
	getAvailableTimeSlotsOutputSchema,
	getChannelStatusOutputSchema,
	getContactSnapshotOutputSchema,
	getConversationMetricsOutputSchema,
	getCurrentDatetimeOutputSchema,
	getDocumentSchemasOutputSchema,
	getEventSnapshotsOutputSchema,
	getKnowledgeGapsOutputSchema,
	getUsageBreakdownOutputSchema,
	getUserDataOutputSchema,
	handoffToHumanOutputSchema,
	ingestUploadedFileOutputSchema,
	joinWaitlistOutputSchema,
	manageAutomationOutputSchema,
	manageBeneficiaryOutputSchema,
	manageContactOutputSchema,
	manageKnowledgeSourceOutputSchema,
	modifyEventRangeOutputSchema,
	modifyEventsOutputSchema,
	queryConversationsOutputSchema,
	queryStructuredDataOutputSchema,
	renderChartOutputSchema,
	requestPhoneNumberOutputSchema,
	scheduleEventOutputSchema,
	scheduleEventsOutputSchema,
	searchAvailableAppointmentsOutputSchema,
	searchConversationsOutputSchema,
	searchDocsOutputSchema,
	sendBusinessLocationOutputSchema,
	sendInteractiveButtonsOutputSchema,
	sendInteractiveListOutputSchema,
	startIntegrationConnectionOutputSchema,
	summarizeConversationOutputSchema,
	ToolSuccessOutputSchema,
	triggerRetrainingOutputSchema,
} from "./tool-output-schemas";

export type {
	AllowableCategories,
	ToolCategory,
	ToolConfigMap,
	ToolRuntimeConfig,
} from "./tool-contract";

// ============================================================================
// Tool Contract Builder
// ============================================================================

/**
 * Create a base tool contract builder with AI SDK metadata support.
 */
const toolContract = oc.$meta<AiSdkToolMeta>({});

// ============================================================================
// Contract Definitions
// ============================================================================

// Using inline definitions to avoid complex type inference issues

/**
 * All tool names in the registry.
 * Defined before TOOL_CONTRACTS so ContractToolName can be used in satisfies.
 */
export const TOOL_NAMES = [
	"getCurrentDatetime",
	"searchDocs",
	"getDocumentSchemas",
	"queryStructuredData",
	"scheduleEvent",
	"joinWaitlist",
	"modifyEvent",
	"cancelEvent",
	"getUserData",
	"getAvailableTimeSlots",
	"searchAvailableAppointments",
	"sendBusinessLocation",
	"sendInteractiveButtons",
	"sendInteractiveList",
	"requestPhoneNumber",
	"handoffToHuman",
	"saveFeedback",
	"collectLead",
	"scheduleEvents",
	"modifyEvents",
	"modifyEventRange",
	"cancelEvents",
	"cancelEventRange",
	"getEventSnapshots",
	"getAvailableSlotsBatch",
	"summarizeConversation",
	"getAnalytics",
	"getUsageBreakdown",
	"getKnowledgeGaps",
	"renderChart",
	"manageKnowledgeSource",
	"ingestUploadedFile",
	"triggerRetraining",
	"getAgentSettings",
	"getContactSnapshot",
	"manageContact",
	"manageBeneficiary",
	"queryConversations",
	"searchConversations",
	"getConversationMetrics",
	"manageAutomation",
	"startIntegrationConnection",
	"getChannelStatus",
	"generateInsightsReport",
] as const;

/**
 * Type for all tool names in the registry.
 */
export type ContractToolName = (typeof TOOL_NAMES)[number];

/**
 * Tool registry entry type for type safety.
 * Every tool must declare a default `needsApproval` value.
 */
export interface ToolRegistryEntry {
	/**
	 * Categories the operator is allowed to drop this tool into via the
	 * ToolsList drag-and-drop UI. When omitted, the only allowable target is
	 * the tool's default `category` (i.e. fixed). When the array contains a
	 * single value, the UI hides the drag handle entirely.
	 */
	allowableCategories?: AllowableCategories;
	/** Approval that an organization override can never disable. */
	approvalLocked?: true | ((input: unknown) => boolean);
	category: ToolCategory;
	/**
	 * Restrict this tool to specific messaging channels.
	 * When set, the tool is only available for conversations on the listed channels.
	 * When omitted, the tool is available on all channels.
	 *
	 * @example channels: ['whatsapp'] // WhatsApp-only tool
	 */
	channels?: readonly string[];
	contract: ContractProcedure<AnySchema, AnySchema, ErrorMap, AiSdkToolMeta>;
	/** Default approval requirement. Runtime config can override this. */
	needsApproval: boolean;
}

/*
 * REMOVED: `requiresStructuredOutput`.
 *
 * It marked five tools (`queryStructuredData`, `saveFeedback`, `collectLead`,
 * `summarizeConversation`, `generateInsightsReport`) as needing the org's model
 * to list OpenRouter's `structured_outputs` parameter; the config page greyed
 * out every other model while any of them was enabled. Nothing on the server
 * ever read the flag, and each case turned out not to hold:
 *
 *  - Tool calling does not use `structured_outputs`. OpenRouter exposes `tools`
 *    (function calling) and `structured_outputs` (`response_format:
 *    json_schema`) as independent `supported_parameters`, and the tool path
 *    never sends a response format — so `queryStructuredData`, `saveFeedback`
 *    and `collectLead` need `tools`, not `structured_outputs`.
 *  - `generateInsightsReport` makes no LLM call at all
 *    (`SystemAgentInsightsService.generateReport` is a Postgres aggregation),
 *    yet ships enabled by default — which is why the gate fired for every org
 *    out of the box.
 *  - `summarizeConversation` does run `Output.object`, but through
 *    `AiAgentService.streamObject`, which takes no model argument and therefore
 *    uses `getDefaultModel()` (the deployment default), never the org's
 *    `llmModel`. Gating the org's model selection could not affect it.
 *
 * The config page now gates on `tools` support instead — see
 * `use-ai-assistant-models.ts`. If a tool ever does need structured outputs on
 * the ORG's model, reintroduce the flag together with the plumbing that threads
 * that model into the nested generation.
 */

export type ToolApprovalSetting =
	| boolean
	| ((input: unknown) => boolean | Promise<boolean>);

/**
 * Central registry of all tool contracts.
 *
 * This is the single source of truth for tool metadata:
 * - Use TOOL_CONTRACTS[name].contract to get the contract
 * - Use TOOL_CONTRACTS[name].category to get the category
 * - Use TOOL_CONTRACTS[name].needsApproval to get the default approval setting
 * - Use Object.entries(TOOL_CONTRACTS) to iterate all tools
 *
 * The `satisfies` constraint ensures:
 * - Every name in TOOL_NAMES has a corresponding entry (compiler error if missing)
 * - Every entry has all required ToolRegistryEntry fields including needsApproval
 */
export const TOOL_CONTRACTS: Record<ContractToolName, ToolRegistryEntry> = {
	// =========================================================================
	// Shared (4)
	// =========================================================================
	getCurrentDatetime: {
		contract: toolContract
			.meta({ [AI_SDK_TOOL_META_SYMBOL]: { strict: true } })
			.route({
				summary:
					"Get current Gregorian/Hijri dates and local time. Call before date/time reasoning.",
			})
			.input(getCurrentDatetimeInputSchema)
			.output(getCurrentDatetimeOutputSchema),
		category: "shared" as const,
		needsApproval: false,
		allowableCategories: ["shared", "assistant", "system-agent"] as const,
	},

	searchDocs: {
		contract: toolContract
			.meta({ [AI_SDK_TOOL_META_SYMBOL]: { strict: true } })
			.route({
				summary:
					"Hybrid knowledge-base search. For whole-document summaries, comparisons, or spanning questions, set fullText=true with documentId.",
			})
			.input(searchDocsInputSchema)
			.output(searchDocsOutputSchema),
		category: "shared" as const,
		needsApproval: false,
		allowableCategories: ["shared", "assistant", "system-agent"] as const,
	},

	getDocumentSchemas: {
		contract: toolContract
			.meta({ [AI_SDK_TOOL_META_SYMBOL]: { strict: true } })
			.route({
				summary:
					"List documents, table schemas, and metadata. Call before queryStructuredData.",
			})
			.input(getDocumentSchemasInputSchema)
			.output(getDocumentSchemasOutputSchema),
		category: "shared" as const,
		needsApproval: false,
		allowableCategories: ["shared", "assistant", "system-agent"] as const,
	},

	queryStructuredData: {
		contract: toolContract
			.meta({ [AI_SDK_TOOL_META_SYMBOL]: { strict: true } })
			.route({
				summary:
					"Run a read-only PostgreSQL SELECT on Excel/CSV rows. First call getDocumentSchemas. Access columns as \"rowData\"->>'ColumnName'; cast numeric values with ::numeric.",
			})
			.input(queryStructuredDataInputSchema)
			.output(queryStructuredDataOutputSchema),
		category: "shared" as const,
		needsApproval: false,
		allowableCategories: ["shared", "assistant", "system-agent"] as const,
	},

	// =========================================================================
	// Assistant (14)
	// =========================================================================
	scheduleEvent: {
		contract: toolContract
			.meta({ [AI_SDK_TOOL_META_SYMBOL]: { strict: true } })
			.route({
				summary:
					"Schedule the current contact into a vacant local slot. If the contact is waiting for that slot, the server completes the waitlist booking automatically. Set beneficiary_name ONLY when the contact is booking on behalf of someone else; omit it for self-bookings. The booking is confirmed only when this tool returns scheduled: true — on an error result, fix the input and retry instead of telling the user it worked. On Instagram, Telegram, and other non-phone channels, first obtain a typed canonical E.164 number or the user's own shared contact; never use a platform user ID, chat ID, or username as a phone number.",
			})
			.input(scheduleEventInputSchema)
			.output(scheduleEventOutputSchema),
		category: "assistant" as const,
		needsApproval: false,
		// Assistant-exclusive: the system agent has its own bulk variant
		// (scheduleEvents), so offering a per-tool agent switch here is noise.
		allowableCategories: ["assistant"] as const,
	},

	joinWaitlist: {
		contract: toolContract
			.meta({ [AI_SDK_TOOL_META_SYMBOL]: { strict: true } })
			.route({
				summary:
					"Waitlist the current contact when no suitable slot exists. Relay the returned position exactly; never invent one. On Instagram, Telegram, and other non-phone channels, first obtain a typed canonical E.164 number or the user's own shared contact; never derive one from platform identifiers.",
			})
			.input(joinWaitlistInputSchema)
			.output(joinWaitlistOutputSchema),
		category: "assistant" as const,
		needsApproval: false,
		allowableCategories: ["assistant"] as const,
	},

	modifyEvent: {
		contract: toolContract
			.meta({ [AI_SDK_TOOL_META_SYMBOL]: { strict: true } })
			.route({
				summary:
					"Modify an event. Call getUserData first for its ID. On Instagram, Telegram, and other non-phone channels, first obtain a typed canonical E.164 number or the user's own shared contact; never derive one from platform identifiers.",
			})
			.input(modifyEventInputSchema)
			.output(ToolSuccessOutputSchema),
		category: "assistant" as const,
		needsApproval: false,
		// Assistant-exclusive: the system agent uses modifyEvents (bulk).
		allowableCategories: ["assistant"] as const,
	},

	cancelEvent: {
		contract: toolContract
			.meta({ [AI_SDK_TOOL_META_SYMBOL]: { strict: true } })
			.route({
				summary:
					"Cancel an event. Call getUserData first for its ID. On Instagram, Telegram, and other non-phone channels, first obtain a typed canonical E.164 number or the user's own shared contact; never derive one from platform identifiers.",
			})
			.input(cancelEventInputSchema)
			.output(ToolSuccessOutputSchema),
		category: "assistant" as const,
		needsApproval: false,
		// Assistant-exclusive: the system agent uses cancelEvents (bulk).
		allowableCategories: ["assistant"] as const,
	},

	getUserData: {
		contract: toolContract
			.meta({ [AI_SDK_TOOL_META_SYMBOL]: { strict: true } })
			.route({
				summary:
					"Get the contact's profile, beneficiaries, and events. Call before booking changes when the target is absent from [contact profile]. On Instagram, Telegram, and other non-phone channels, first obtain a typed canonical E.164 number or the user's own shared contact; never use a platform user ID, chat ID, or username as a phone number.",
			})
			.input(getUserDataInputSchema)
			.output(getUserDataOutputSchema),
		category: "assistant" as const,
		needsApproval: false,
		// Assistant-exclusive: the system agent uses getContactSnapshot /
		// getEventSnapshots for the same reads.
		allowableCategories: ["assistant"] as const,
	},

	getAvailableTimeSlots: {
		contract: toolContract
			.meta({ [AI_SDK_TOOL_META_SYMBOL]: { strict: true } })
			.route({
				summary:
					"Get a date's available 24-hour slots. Never offer/book dates rejected by note as beyond the booking horizon.",
			})
			.input(getAvailableTimeSlotsInputSchema)
			.output(getAvailableTimeSlotsOutputSchema),
		category: "assistant" as const,
		needsApproval: false,
		// Assistant-exclusive: the system agent uses getAvailableSlotsBatch.
		allowableCategories: ["assistant"] as const,
	},

	searchAvailableAppointments: {
		contract: toolContract
			.meta({ [AI_SDK_TOOL_META_SYMBOL]: { strict: true } })
			.route({
				summary:
					"Find available appointments across days. Use for next-slot requests. Never offer/book dates omitted by the booking-horizon note.",
			})
			.input(searchAvailableAppointmentsInputSchema)
			.output(searchAvailableAppointmentsOutputSchema),
		category: "assistant" as const,
		needsApproval: false,
		// Assistant-exclusive: the system agent uses getAvailableSlotsBatch.
		allowableCategories: ["assistant"] as const,
	},

	sendBusinessLocation: {
		contract: toolContract
			.meta({ [AI_SDK_TOOL_META_SYMBOL]: { strict: true } })
			.route({
				summary:
					"Send the business location for address/direction requests. The pin itself carries the business name and address, so do not also type them out — add at most one short line after it, or nothing.",
			})
			.input(sendBusinessLocationInputSchema)
			.output(sendBusinessLocationOutputSchema),
		category: "assistant" as const,
		needsApproval: false,
		channels: ["whatsapp", "telegram"] as const,
		allowableCategories: ["assistant"] as const,
	},

	sendInteractiveButtons: {
		contract: toolContract
			.meta({ [AI_SDK_TOOL_META_SYMBOL]: { strict: true } })
			.route({
				summary:
					"Send 1–3 tappable reply buttons. Always prefer this over typing the options into your message text: any confirmation, yes/no, or short choice should be offered as buttons, never as a written list the user has to answer in words. body.text IS the message the customer receives, so put the whole question there and do not repeat it. When this returns ok the message is already delivered — end your turn with no further text. WhatsApp uses reply buttons, Telegram uses inline buttons, and Instagram uses postback button templates. Use this for ≤3 options; use sendInteractiveList for 4–10.",
			})
			.input(sendInteractiveButtonsInputSchema)
			.output(sendInteractiveButtonsOutputSchema),
		category: "assistant" as const,
		needsApproval: false,
		channels: ["whatsapp", "telegram", "instagram"] as const,
		allowableCategories: ["assistant"] as const,
	},

	sendInteractiveList: {
		contract: toolContract
			.meta({ [AI_SDK_TOOL_META_SYMBOL]: { strict: true } })
			.route({
				summary:
					"Send 4–10 tappable single-select options. Always prefer this over typing the options into your message text: never write a numbered or bulleted list of choices and ask the user to reply with one. body.text IS the message the customer receives, so put the whole question there and do not repeat it. When this returns ok the message is already delivered — end your turn with no further text. WhatsApp uses a list menu, Telegram uses inline buttons, and Instagram uses text quick replies. Instagram quick replies are mobile-only, so keep the choices readable in the message text. Limits: 10 sections and 10 total rows; use sendInteractiveButtons for ≤3 options.",
			})
			.input(sendInteractiveListInputSchema)
			.output(sendInteractiveListOutputSchema),
		category: "assistant" as const,
		needsApproval: false,
		channels: ["whatsapp", "telegram", "instagram"] as const,
		allowableCategories: ["assistant"] as const,
	},

	requestPhoneNumber: {
		contract: toolContract
			.meta({ [AI_SDK_TOOL_META_SYMBOL]: { strict: true } })
			.route({
				summary:
					"Ask the user for their phone number when a phone-required action cannot continue. This tool writes and sends the request itself, so pass your wording in `message` rather than typing the ask into your reply; when it returns ok the request is already delivered — end your turn with no further text. Telegram private chats use a native contact button. Instagram uses a profile-phone quick reply when available, plus an explicit typed canonical E.164 fallback because the button is mobile-only and may be absent.",
			})
			.input(requestPhoneNumberInputSchema)
			.output(requestPhoneNumberOutputSchema),
		category: "assistant" as const,
		needsApproval: false,
		channels: ["telegram", "instagram"] as const,
		allowableCategories: ["assistant"] as const,
	},

	handoffToHuman: {
		contract: toolContract
			.meta({ [AI_SDK_TOOL_META_SYMBOL]: { strict: true } })
			.route({
				summary:
					"Hand off to a human and disable AI replies. Use on explicit request, clear frustration, or unresolved legal/medical/billing/complaint cases. Then send one reassurance and stop. Idempotent.",
			})
			.input(handoffToHumanInputSchema)
			.output(handoffToHumanOutputSchema),
		category: "assistant" as const,
		needsApproval: false,
		allowableCategories: ["assistant"] as const,
	},

	saveFeedback: {
		contract: toolContract
			.meta({ [AI_SDK_TOOL_META_SYMBOL]: { strict: true } })
			.route({
				summary:
					"Record unsolicited customer praise, complaints, incidents, frustration, or opinions. Never request feedback; skip routine facts/scheduling. quote must be verbatim; summary is concise in operator language. Then acknowledge briefly and continue.",
			})
			.input(saveFeedbackInputSchema)
			.output(ToolSuccessOutputSchema),
		category: "assistant" as const,
		needsApproval: false,
		allowableCategories: ["assistant"] as const,
	},

	/**
	 * `collectLead` — DYNAMIC, PER-ORG INPUT SCHEMA.
	 *
	 * The operator defines the field set in the config UI; the tool factory
	 * rebuilds the LLM-facing Zod schema at runtime from those definitions
	 * (`createCollectLeadTool` injects `inputSchema` into `implementTool`,
	 * overriding the permissive contract schema). When the operator has not
	 * defined any fields, `createCollectLeadTool` returns null so the tool is
	 * not exposed to the LLM.
	 */
	collectLead: {
		contract: toolContract
			.meta({ [AI_SDK_TOOL_META_SYMBOL]: { strict: true } })
			.route({
				summary:
					"Collect an interested customer's lead only after normal conversation supplies every schema field. Never interrogate or invent. Pass values verbatim after trimming and E.164 phone formatting; every shown field is required.",
			})
			.input(collectLeadInputSchema)
			.output(ToolSuccessOutputSchema),
		category: "assistant" as const,
		needsApproval: false,
		allowableCategories: ["assistant"] as const,
	},

	// =========================================================================
	// System-Agent (8)
	// NOTE: strict mode not enabled for system-agent tools due to optional properties
	// =========================================================================
	scheduleEvents: {
		contract: toolContract
			.route({
				summary: "Schedule events for multiple users, grouped by phone.",
			})
			.input(scheduleEventsInputSchema)
			.output(scheduleEventsOutputSchema),
		category: "system-agent" as const,
		// Booked events carry their OWN approval: an event type with
		// `requiresApproval` lands the booking PENDING, holding its dates until
		// the owner approves it (`resolveInitialBookingState`). Gating the tool
		// on top of that gates the wrong thing — it blocks before the event
		// exists, so the operator is asked to approve a booking they cannot see
		// on the calendar, and nothing is held in the meantime. Per-type approval
		// is the gate; this one only duplicated it.
		needsApproval: false,
		allowableCategories: ["system-agent"] as const,
	},

	modifyEvents: {
		contract: toolContract
			.route({
				summary: "Modify events by known IDs.",
			})
			.input(modifyEventsInputSchema)
			.output(modifyEventsOutputSchema),
		category: "system-agent" as const,
		needsApproval: true,
		allowableCategories: ["system-agent"] as const,
	},

	modifyEventRange: {
		contract: toolContract
			.route({
				summary: "Shift or change event types across a date range.",
			})
			.input(modifyEventRangeInputSchema)
			.output(modifyEventRangeOutputSchema),
		category: "system-agent" as const,
		needsApproval: true,
		allowableCategories: ["system-agent"] as const,
	},

	cancelEvents: {
		contract: toolContract
			.route({
				summary: "Cancel events by known IDs.",
			})
			.input(cancelEventsInputSchema)
			.output(cancelEventsOutputSchema),
		category: "system-agent" as const,
		needsApproval: true,
		allowableCategories: ["system-agent"] as const,
	},

	cancelEventRange: {
		contract: toolContract
			.route({
				summary: "Cancel matching events across a date range.",
			})
			.input(cancelEventRangeInputSchema)
			.output(cancelEventRangeOutputSchema),
		category: "system-agent" as const,
		needsApproval: true,
		allowableCategories: ["system-agent"] as const,
	},

	getEventSnapshots: {
		contract: toolContract
			.route({
				summary: "Query filtered date ranges; returns events by user.",
			})
			.input(getEventSnapshotsInputSchema)
			.output(getEventSnapshotsOutputSchema),
		category: "system-agent" as const,
		needsApproval: false,
		allowableCategories: ["system-agent"] as const,
	},

	getAvailableSlotsBatch: {
		contract: toolContract
			.route({
				summary:
					"Get availability for multiple dates. Never offer/book dates omitted by the booking-horizon note.",
			})
			.input(getAvailableSlotsBatchInputSchema)
			.output(getAvailableSlotsBatchOutputSchema),
		category: "system-agent" as const,
		needsApproval: false,
		allowableCategories: ["system-agent"] as const,
	},

	summarizeConversation: {
		contract: toolContract
			.route({
				summary:
					"Summarize a customer conversation on operator request. For a contact, pass contactPhoneE164 (preferred) or contactId; never paste transcripts because the server loads history. Render the returned structured summary directly.",
			})
			.input(summarizeConversationInputSchema)
			.output(summarizeConversationOutputSchema),
		category: "system-agent" as const,
		needsApproval: false,
		// Operator-facing summarization is a system-agent duty; exposing it to
		// the customer assistant was never meaningful.
		allowableCategories: ["system-agent"] as const,
	},

	getAnalytics: {
		contract: toolContract
			.route({
				summary:
					"Answer an analytics question from organization data for a range of at most six months.",
			})
			.input(getAnalyticsInputSchema)
			.output(getAnalyticsOutputSchema),
		category: "system-agent",
		needsApproval: false,
		allowableCategories: ["system-agent"],
	},
	getUsageBreakdown: {
		contract: toolContract
			.route({
				summary:
					"Read AI usage, credits, and the caller's remaining daily system-agent messages.",
			})
			.input(getUsageBreakdownInputSchema)
			.output(getUsageBreakdownOutputSchema),
		category: "system-agent",
		needsApproval: false,
		allowableCategories: ["system-agent"],
	},
	getKnowledgeGaps: {
		contract: toolContract
			.route({
				summary: "List recurring unanswered questions and knowledge gaps.",
			})
			.input(getKnowledgeGapsInputSchema)
			.output(getKnowledgeGapsOutputSchema),
		category: "system-agent",
		needsApproval: false,
		allowableCategories: ["system-agent"],
	},
	renderChart: {
		contract: toolContract
			.route({
				summary:
					"Render a validated bar, line, area, or pie chart in chat. Use only data returned by read tools.",
			})
			.input(renderChartInputSchema)
			.output(renderChartOutputSchema),
		category: "system-agent",
		needsApproval: false,
		allowableCategories: ["system-agent"],
	},
	manageKnowledgeSource: {
		contract: toolContract
			.route({
				summary:
					"Propose one knowledge-source add, update, delete, or enablement change.",
			})
			.input(manageKnowledgeSourceInputSchema)
			.output(manageKnowledgeSourceOutputSchema),
		category: "system-agent",
		needsApproval: true,
		allowableCategories: ["system-agent"],
	},
	ingestUploadedFile: {
		contract: toolContract
			.route({
				summary:
					"Approve ingestion of one staged chat upload into the knowledge base.",
			})
			.input(ingestUploadedFileInputSchema)
			.output(ingestUploadedFileOutputSchema),
		category: "system-agent",
		needsApproval: true,
		allowableCategories: ["system-agent"],
	},
	triggerRetraining: {
		contract: toolContract
			.route({
				summary: "Approve retraining of all enabled knowledge sources.",
			})
			.input(triggerRetrainingInputSchema)
			.output(triggerRetrainingOutputSchema),
		category: "system-agent",
		needsApproval: true,
		allowableCategories: ["system-agent"],
	},
	getAgentSettings: {
		contract: toolContract
			.route({
				summary:
					"Read whitelisted assistant, conversation, and channel settings. This tool cannot change settings.",
			})
			.input(getAgentSettingsInputSchema)
			.output(getAgentSettingsOutputSchema),
		category: "system-agent",
		needsApproval: false,
		allowableCategories: ["system-agent"],
	},
	getContactSnapshot: {
		contract: toolContract
			.route({
				summary:
					"Read a contact or beneficiary snapshot. Call for every party before proposing delete, merge, or separation.",
			})
			.input(getContactSnapshotInputSchema)
			.output(getContactSnapshotOutputSchema),
		category: "system-agent",
		needsApproval: false,
		allowableCategories: ["system-agent"],
	},
	manageContact: {
		contract: toolContract
			.route({
				summary:
					"Propose one contact update or soft delete. Deletes always require approval.",
			})
			.input(manageContactInputSchema)
			.output(manageContactOutputSchema),
		category: "system-agent",
		needsApproval: true,
		approvalLocked: (input) =>
			typeof input === "object" &&
			input !== null &&
			"op" in input &&
			input.op === "delete",
		allowableCategories: ["system-agent"],
	},
	manageBeneficiary: {
		contract: toolContract
			.route({
				summary:
					"Propose one beneficiary separation or merge. Every merge always requires approval.",
			})
			.input(manageBeneficiaryInputSchema)
			.output(manageBeneficiaryOutputSchema),
		category: "system-agent",
		needsApproval: true,
		approvalLocked: (input) =>
			typeof input === "object" &&
			input !== null &&
			"op" in input &&
			input.op !== "separate",
		allowableCategories: ["system-agent"],
	},
	queryConversations: {
		contract: toolContract
			.route({
				summary:
					"Query organization conversations by operational status, operator, and date. Read-only.",
			})
			.input(queryConversationsInputSchema)
			.output(queryConversationsOutputSchema),
		category: "system-agent",
		needsApproval: false,
		allowableCategories: ["system-agent"],
	},
	searchConversations: {
		contract: toolContract
			.route({
				summary:
					"Search organization conversation messages using text or semantic search. Read-only.",
			})
			.input(searchConversationsInputSchema)
			.output(searchConversationsOutputSchema),
		category: "system-agent",
		needsApproval: false,
		allowableCategories: ["system-agent"],
	},
	getConversationMetrics: {
		contract: toolContract
			.route({
				summary:
					"Read conversation response, handoff, feedback, resolution, and operator workload metrics.",
			})
			.input(getConversationMetricsInputSchema)
			.output(getConversationMetricsOutputSchema),
		category: "system-agent",
		needsApproval: false,
		allowableCategories: ["system-agent"],
	},
	manageAutomation: {
		contract: toolContract
			.route({
				summary:
					"Propose one webhook automation create, update, enable, disable, or delete operation.",
			})
			.input(manageAutomationInputSchema)
			.output(manageAutomationOutputSchema),
		category: "system-agent",
		needsApproval: true,
		allowableCategories: ["system-agent"],
	},
	startIntegrationConnection: {
		contract: toolContract
			.route({
				summary:
					"Create a short-lived configuration link. The connection is completed by the operator in configuration.",
			})
			.input(startIntegrationConnectionInputSchema)
			.output(startIntegrationConnectionOutputSchema),
		category: "system-agent",
		needsApproval: false,
		allowableCategories: ["system-agent"],
	},
	getChannelStatus: {
		contract: toolContract
			.route({
				summary:
					"Read WhatsApp, Telegram, Instagram, and system-agent channel readiness. This tool cannot toggle channels.",
			})
			.input(getChannelStatusInputSchema)
			.output(getChannelStatusOutputSchema),
		category: "system-agent",
		needsApproval: false,
		allowableCategories: ["system-agent"],
	},
	generateInsightsReport: {
		contract: toolContract
			.route({
				summary:
					"Generate a structured cross-functional insights report from analytics, usage, gaps, and conversations.",
			})
			.input(generateInsightsReportInputSchema)
			.output(generateInsightsReportOutputSchema),
		category: "system-agent",
		needsApproval: false,
		allowableCategories: ["system-agent"],
	},
};

/**
 * Get all tools for a specific category.
 *
 * @param category - The category to filter by ('assistant' or 'system-agent')
 * @returns Array of [name, entry] tuples for tools in that category
 */
export function getToolsByCategory(
	category: "assistant" | "system-agent"
): [string, ToolRegistryEntry][] {
	return Object.entries(TOOL_CONTRACTS).filter(
		([, { category: cat }]) => cat === category || cat === "shared"
	);
}

/**
 * Get tool metadata for display/configuration purposes.
 */
export function getToolMetadata(): Array<{
	name: string;
	category: ToolCategory;
	description: string;
	channels?: readonly string[];
}> {
	return Object.entries(TOOL_CONTRACTS).map(
		([name, { contract, category, channels }]) => {
			const def = contract["~orpc"];
			const description = def.route.summary ?? def.route.description ?? "";

			return {
				name,
				category,
				description,
				...(channels ? { channels } : {}),
			};
		}
	);
}

/**
 * Get the human-facing summary for a tool from its contract route.
 *
 * Single source of truth for a tool's `description` as seen by the LLM.
 * Provider agents import this and use it as each tool's `description` so the
 * summary text lives in exactly one place (the contract) rather than being
 * re-typed at every tool factory.
 *
 * @param name - The tool name from the contract registry
 * @returns The contract route summary, or '' when none is set.
 */
export function getToolSummary(name: ContractToolName): string {
	return TOOL_CONTRACTS[name].contract["~orpc"].route.summary ?? "";
}

/**
 * Resolve the effective `needsApproval` value for a tool.
 *
 * Priority: runtime config override > contract default.
 * Always returns a defined boolean -- never undefined.
 *
 * @param toolName - The tool name from the contract registry
 * @param configOverride - Optional runtime override from app config
 */
export function resolveNeedsApproval(
	toolName: ContractToolName,
	configOverride?: boolean,
	input?: unknown
): boolean {
	const locked = TOOL_CONTRACTS[toolName].approvalLocked;
	if (
		locked === true ||
		(typeof locked === "function" && input !== undefined && locked(input))
	) {
		return true;
	}
	if (configOverride !== undefined) {
		return configOverride;
	}
	return TOOL_CONTRACTS[toolName].needsApproval;
}

/** Resolve the AI SDK setting, retaining per-input locked approval predicates. */
export function resolveNeedsApprovalSetting(
	toolName: ContractToolName,
	configOverride?: boolean
): ToolApprovalSetting {
	const locked = TOOL_CONTRACTS[toolName].approvalLocked;
	if (locked === true) {
		return true;
	}
	if (typeof locked === "function") {
		return (input) => resolveNeedsApproval(toolName, configOverride, input);
	}
	return resolveNeedsApproval(toolName, configOverride);
}
